import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MODULES } from "../../data/landingContent";
import Reveal from "./Reveal";
import useLiquidGlow from "../../hooks/useLiquidGlow";

function ModuleCardBody({ m, index }) {
  return (
    <>
      <div className="top">
        <span className="ic" aria-hidden="true">
          {m.icon}
        </span>
        <span className="idx">{String(index + 1).padStart(2, "0")}</span>
      </div>
      <div>
        <h3>{m.title}</h3>
        <p>{m.desc}</p>
        {m.badge && <span className="badge">{m.badge}</span>}
      </div>
    </>
  );
}

function ModuleCard({ module: m, index }) {
  const cardRef = useRef(null);
  const hasDetailPage = Boolean(m.link);

  const onCardMove = useCallback((e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty("--mx", `${x}%`);
    el.style.setProperty("--my", `${y}%`);
  }, []);

  const onCardLeave = useCallback(() => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty("--mx", "50%");
    el.style.setProperty("--my", "50%");
  }, []);

  const className = [
    "module-card",
    m.accent ? "accent" : "",
    m.featured ? "featured" : "",
    hasDetailPage ? "module-card--linked" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const inner = (
    <>
      <span className="module-card-glass" aria-hidden="true" />
      <div className="module-card-body">
        <ModuleCardBody m={m} index={index} />
      </div>
      {hasDetailPage && (
        <span className="module-card-cta">
          See details
          <span className="module-card-cta-arrow" aria-hidden="true">
            →
          </span>
        </span>
      )}
    </>
  );

  if (hasDetailPage) {
    return (
      <Link
        ref={cardRef}
        to={m.link}
        className={className}
        aria-label={`${m.title} — see details`}
        onMouseMove={onCardMove}
        onMouseLeave={onCardLeave}
      >
        {inner}
      </Link>
    );
  }

  return (
    <article
      ref={cardRef}
      className={className}
      onMouseMove={onCardMove}
      onMouseLeave={onCardLeave}
    >
      {inner}
    </article>
  );
}

export default function Modules({ showHeader = true, id = "modules" }) {
  const { visible, onMove, onLeave, zoneRef } = useLiquidGlow();

  return (
    <section id={id} aria-labelledby="modules-heading">
      <div className="wrap">
        {showHeader && (
          <>
            <span className="kicker">The platform</span>
            <h2 className="section-title" id="modules-heading">
              Every department, <b>one connected record</b>
            </h2>
            <p className="section-lede">
              Front desk, doctors, pharmacy, and billing all work from the same patient record —
              nothing re-typed, nothing lost between departments.{" "}
              <span className="modules-hint">
                Physio, nutrition, and agent cards with a detail page show See details.
              </span>
            </p>
          </>
        )}

        <Reveal as="div" className="modules-grid-shell reveal-stagger">
          <div
            ref={zoneRef}
            className="modules-grid liquid-glass-zone"
            onMouseMove={onMove}
            onMouseLeave={onLeave}
          >
            <span
              className={`liquid-glass-glow ${visible ? "is-visible" : ""}`}
              aria-hidden="true"
            />
            {MODULES.map((m, i) => (
              <ModuleCard key={m.title} module={m} index={i} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
